'use client'

import Image from 'next/image'
import { useState } from 'react'
import { LuPlay } from 'react-icons/lu'

import { HeroBannerVideoModal } from './HeroBannerVideoModal'
import { ViewProjectsButton } from './ViewProjectsButton'

export type FeatureIconKey = 'Monitor' | 'Hexagon' | 'FileText'

export interface FeatureItem {
  iconKey: FeatureIconKey
  title: string
  alt?: string
}

const FEATURES: Record<AppLocale, FeatureItem[]> = {
  en: [
    {
      iconKey: 'Monitor',
      title: 'GALLIUM TODAY,GALLIUM TOMORROW',
      alt: 'Monitor settings icon',
    },
    {
      iconKey: 'Hexagon',
      title: 'GALLIUM NEVER ALONE',
      alt: 'Hexagonal pattern icon',
    },
    {
      iconKey: 'FileText',
      title: 'GO FAR WITH GALLIUM',
      alt: 'Document icon',
    },
    {
      iconKey: 'Monitor',
      title: 'GALLIUM TODAY,GALLIUM TOMORROW',
      alt: 'Monitor settings icon',
    },
  ],
  fr: [
    {
      iconKey: 'Monitor',
      title: "GALLIUM AUJOURD'HUI, GALLIUM DEMAIN",
      alt: 'Icône de réglages moniteur',
    },
    {
      iconKey: 'Hexagon',
      title: 'GALLIUM JAMAIS SEUL',
      alt: 'Icône de motif hexagonal',
    },
    {
      iconKey: 'FileText',
      title: 'ALLEZ PLUS LOIN AVEC GALLIUM',
      alt: 'Icône de document',
    },
    {
      iconKey: 'Monitor',
      title: "GALLIUM AUJOURD'HUI, GALLIUM DEMAIN",
      alt: 'Icône de réglages moniteur',
    },
  ],
  ar: [
    {
      iconKey: 'Monitor',
      title: 'جاليوم اليوم، جاليوم الغد',
      alt: 'أيقونة إعدادات الشاشة',
    },
    {
      iconKey: 'Hexagon',
      title: 'جاليوم لا يكون وحيدا',
      alt: 'أيقونة نمط سداسي',
    },
    {
      iconKey: 'FileText',
      title: 'انطلق أبعد مع جاليوم',
      alt: 'أيقونة مستند',
    },
    {
      iconKey: 'Monitor',
      title: 'جاليوم اليوم، جاليوم الغد',
      alt: 'أيقونة إعدادات الشاشة',
    },
  ],
}

const ICON_MAP: Record<FeatureIconKey, string> = {
  Monitor: '/icon/Monitor.svg',
  Hexagon: '/icon/Gear.svg',
  FileText: '/icon/BookWithGear.svg',
}

const DEFAULT_VIDEO_SRC = '/HomePageVideo.mp4'

export type FeatureItemCMS = {
  title: string
  id?: string | null
}

export type FeaturesSectionProps = {
  locale: AppLocale
  /** Center the two-column layout and cards. */
  center?: boolean
  /** Use smaller cards, text, and image. */
  compact?: boolean
  /** From Home global features.items; when set, overrides static FEATURES. */
  featureItems?: FeatureItemCMS[] | null
}

/**
 * Two-column Features section: image left (FEATURESimage.svg), four white
 * cards with icons and bold uppercase text on the right. Rendered after
 * HeroHeadingSection on the homepage.
 */
export function FeaturesSection({
  locale,
  center = false,
  compact = false,
  featureItems,
}: FeaturesSectionProps) {
  const [modalOpen, setModalOpen] = useState(false)

  const useCMS = featureItems && featureItems.length > 0
  const items = useCMS
    ? featureItems.map((item, i) => ({
        iconUrl: [ICON_MAP.Monitor, ICON_MAP.Hexagon, ICON_MAP.FileText][i % 3],
        title: item.title ?? '',
        iconAlt:
          [FEATURES[locale][0]?.alt, FEATURES[locale][1]?.alt, FEATURES[locale][2]?.alt][i % 3] ??
          '',
      }))
    : FEATURES[locale].map((f) => ({
        iconUrl: ICON_MAP[f.iconKey],
        title: f.title,
        iconAlt: f.alt ?? '',
      }))
  return (
    <section
      className={`features-section bg-white pt-[30px] pb-16 lg:pb-24 ${compact ? 'pt-5 pb-10 lg:pb-16' : ''}`}
      aria-labelledby="features-two-col-heading"
      dir={locale === 'ar' ? 'rtl' : 'ltr'}
    >
      <div className="mx-auto w-full min-w-0 max-w-7xl px-4 max-[400px]:px-3 sm:px-6 md:max-w-[94%] md:px-5 lg:max-w-7xl lg:px-8 overflow-x-hidden features-section-inner">
        {/* CTA */}
        <div className="text-left mb-6 lg:mb-8 ">
          <h2 id="features-two-col-heading" className="sr-only">
            Features
          </h2>
          <ViewProjectsButton />
        </div>
        <div
          className={`flex min-w-0 flex-col gap-4 lg:flex-row lg:gap-5 ${
            center ? 'items-center lg:items-center justify-center' : 'items-stretch lg:items-start'
          }`}
        >
          {/* Left column - image (445×543px per design) */}
          <button
            type="button"
            onClick={() => setModalOpen(true)}
            aria-label="Play video"
            className={`group relative w-full min-w-0 flex-shrink-0 overflow-hidden rounded-none bg-slate-600  ${
              compact
                ? 'h-[200px] max-[400px]:h-[180px] sm:h-[320px] lg:h-[470px] lg:w-[360px]'
                : 'h-[260px] max-[400px]:h-[220px] sm:h-[360px] lg:h-[522px] lg:w-[445px]'
            } cursor-pointer border-0 p-0 text-left block`}
          >
            <Image
              src="/feature/FEATURES.svg"
              alt="Industrial welding and manufacturing process"
              width={445}
              height={543}
              className="h-full w-full object-cover object-center"
              sizes="(max-width: 1023px) 100vw, 445px"
              priority={false}
            />
            {/* Red overlay that fills from left to right on hover */}
            <div className="features-image-overlay absolute inset-0 bg-[#E60023] z-[1]" />
            {/* Play button that appears on hover */}
            <div className="features-play-button absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100 z-[30]">
              <span
                aria-hidden
                className="relative flex items-center justify-center w-14 h-14 shrink-0 rounded-full bg-white focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 shadow-md transition-transform duration-300 ease-in-out hover:scale-105 active:scale-95 overflow-hidden"
                style={{ backgroundColor: '#ffffff', isolation: 'isolate' }}
              >
                <div className="absolute inset-0 bg-white rounded-full z-0" />
                <LuPlay
                  size={24}
                  aria-hidden
                  className="ml-1 relative z-10"
                  style={{ color: '#E60023', fill: '#E60023' }}
                />
              </span>
            </div>
          </button>

          {/* Right column - feature cards */}
          <div
            className={`flex min-w-0 flex-1 flex-col gap-[5px] p-6 pt-0 md:px-0 md:pt-0 md:pb-0 lg:justify-between lg:gap-0 lg:p-0 lg:pl-0 ${compact ? 'lg:h-[470px]' : 'lg:h-[522px]'} ${!compact ? 'bg-[#F9F9F9]' : ''} features-cards-wrapper`}
          >
            {items.map((feature, index) => (
              <article
                key={useCMS ? `cms-${index}` : `static-${index}`}
                className={`group flex w-full flex-shrink-0 items-center overflow-hidden rounded-none shadow-md transition-all duration-300 hover:scale-[1.02] hover:shadow-lg opacity-100 lg:w-3/4 ${
                  compact
                    ? 'min-h-[90px] lg:min-h-[110px] gap-[10px] px-[33px] py-[25px] lg:gap-[10px]'
                    : 'h-[100px] lg:h-[123px] min-w-0 max-w-[868px] gap-[10px] pt-4 pr-4 pb-4 pl-4 lg:pt-[25px] lg:pr-[33px] lg:pb-[25px] lg:pl-[33px]'
                }`}
              >
                <div
                  className={`relative flex flex-shrink-0 items-center justify-center rounded-full bg-[#F6F6F6] transition-colors duration-300 group-hover:bg-[#e00000] ${
                    compact ? 'h-14 w-14' : 'h-14 w-14 lg:h-[72px] lg:w-[72px]'
                  }`}
                  aria-hidden
                >
                  <Image
                    src={feature.iconUrl ?? ICON_MAP.Monitor}
                    alt={feature.iconAlt || 'Feature icon'}
                    width={34}
                    height={30}
                    className="features-icon transition-all duration-300 brightness-0 saturate-100"
                    style={{
                      width: compact ? 26 : 34,
                      height: compact ? 23 : 30,
                      opacity: 1,
                    }}
                    aria-hidden
                  />
                </div>
                <h3
                  className={`min-w-0 flex-1 font-bebas-neue font-medium text-[#212121] leading-tight tracking-normal capitalize align-middle whitespace-nowrap overflow-x-auto overflow-y-hidden ${
                    compact
                      ? 'text-xl sm:text-2xl lg:text-3xl lg:leading-tight'
                      : 'text-xl sm:text-2xl lg:text-3xl lg:text-[36px] lg:leading-tight'
                  }`}
                >
                  {feature.title}
                </h3>
              </article>
            ))}
          </div>
        </div>
      </div>
      <HeroBannerVideoModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        videoSrc={DEFAULT_VIDEO_SRC}
      />
    </section>
  )
}
